
import { useEffect, useState, useRef } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../hooks/useAuth';
import type { Notification } from '../../hooks/useNotifications';

export function NotificationToast() {
  const { user } = useAuth();
  const { notifications } = useNotifications(user?.id ?? null);
  const [visibleToasts, setVisibleToasts] = useState<Notification[]>([]);

  // Keep references to active timers so we can clean them up on unmount or when a toast is removed
  const timersRef = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    // Show only the latest unread notification as a toast
    const latestUnread = notifications.find((n) => !n.isRead);

    if (
      latestUnread &&
      !visibleToasts.find((t) => t.id === latestUnread.id)
    ) {
      setVisibleToasts((prev) => [...prev, latestUnread]);

      // Auto‑hide after 5 seconds
      const timer = setTimeout(() => {
        setVisibleToasts((prev) =>
          prev.filter((t) => t.id !== latestUnread.id)
        );
        delete timersRef.current[latestUnread.id];
      }, 5000);

      // Store timer so it can be cleared later if needed
      timersRef.current[latestUnread.id] = timer;
    }

    // Cleanup any timers for toasts that are no longer present
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
    };
    // We only want to react when the notifications list changes.
    // `visibleToasts` is intentionally omitted to avoid re‑triggering on every render.
  }, [notifications]);

  const handleClose = (toastId: string) => {
    // Clear any pending auto‑hide timer
    if (timersRef.current[toastId]) {
      clearTimeout(timersRef.current[toastId]);
      delete timersRef.current[toastId];
    }
    setVisibleToasts((prev) => prev.filter((t) => t.id !== toastId));
  };

  const handleClick = (notification: Notification) => {
    try {
      // Navigate based on notification type using a global navigation helper if it exists
      if (notification.type === 'message') {
        window.REACT_APP_NAVIGATE?.('/messages');
      } else if (notification.type === 'swap_proposal') {
        window.REACT_APP_NAVIGATE?.('/profile');
      }
    } catch (err) {
      // Fail‑soft: log the error but keep the UI functional
      console.error('Navigation error in NotificationToast:', err);
    } finally {
      handleClose(notification.id);
    }
  };

  if (visibleToasts.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 space-y-3 pointer-events-none">
      {visibleToasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto w-96 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden animate-slide-in-right"
        >
          <div
            onClick={() => handleClick(toast)}
            className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${
                  toast.type === 'message' ? 'bg-blue-100' : 'bg-teal-100'
                }`}
              >
                <i
                  className={`${
                    toast.type === 'message'
                      ? 'ri-message-3-line text-blue-600'
                      : 'ri-swap-line text-teal-600'
                  } text-lg`}
                ></i>
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {toast.title}
                </p>
                <p className="text-sm text-gray-600">{toast.message}</p>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose(toast.id);
                }}
                className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-gray-100">
            <div className="h-full bg-teal-500 animate-progress-bar"></div>
          </div>
        </div>
      ))}
    </div>
  );
}
